import { useTu } from "@src/appV2/i18n/useTu";
import { Button, type SxProps, type Theme } from "@mui/material";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { canFetchPlacementApplications } from "@src/appV2/PlacementApplications/api/useFetchPaginatedPlacementApplications";

import {
  getCandidateUrlPath,
  PLACEMENT_CANDIDATES_LINK_SOURCE_EVENTS,
} from "@src/appV2/PlacementCandidates/utils";

import { getUserType } from "@src/appV2/PlacementCandidates/utils/helper";
import { useOpenUrl } from "@src/mobile/utils/openUrl";
import { useCallback } from "react";
import type { AdminChatMessage, PlacementChannelMetadata } from "../types";
import { parseCustomMessage } from "../utils/parseCustomMessage";
import { ChatMessageCard } from "./ChatMessageCard";

interface PlacementApplicationMessageProps {
  message: AdminChatMessage;
  channelUrl: string;
  channelMetadata?: PlacementChannelMetadata;
  sx?: SxProps<Theme>;
}

/**
 * Renders a placement application message with "View profile" button.
 * Shown when a worker applies for or shows interest in a placement.
 */
export function PlacementApplicationMessage(props: PlacementApplicationMessageProps) {
  const { tu } = useTu("chat");

  const { message, channelUrl, channelMetadata, sx } = props;
  const { admin, profile, user } = useSession();
  const openUrl = useOpenUrl();
  const displayMessage = parseCustomMessage(message.message);

  const { hcpName, placementTitle, facilityName, placementCandidateAccessId, managingFacilityId } =
    channelMetadata ?? {};

  const [workplaceId, workerId, placementId] = channelUrl.split("_") as Array<string | undefined>;

  // Build a contextual message if we have all the metadata
  const contextualMessage =
    hcpName && placementTitle && facilityName
      ? `${hcpName} has applied for the ${placementTitle} opening at ${facilityName}. View their candidate profile to check their resume, license and pre-employment credentials.`
      : displayMessage;

  const handleViewProfile = useCallback(() => {
    if (!placementCandidateAccessId) {
      return;
    }

    logEvent(APP_V2_USER_EVENTS.PLACEMENT_CANDIDATE_CHAT_ACTION, {
      workplaceId,
      managingWorkplaceId: managingFacilityId,
      workerId,
      placementId,
      userId: user?._id,
      userType: getUserType(profile, admin),
      userEmail: user?.email,
      source: "chatBody",
      action: "viewProfileButtonClick",
    });

    const candidateUrl = getCandidateUrlPath(
      placementCandidateAccessId,
      profile?.userId,
      PLACEMENT_CANDIDATES_LINK_SOURCE_EVENTS.PLACEMENT_CANDIDATE_CHAT_MESSAGE
    );

    void openUrl(candidateUrl);
  }, [
    placementCandidateAccessId,
    workplaceId,
    managingFacilityId,
    workerId,
    placementId,
    user?._id,
    user?.email,
    profile,
    admin,
    openUrl,
  ]);

  return (
    <ChatMessageCard
      actions={
        placementCandidateAccessId &&
        (admin || canFetchPlacementApplications(user?.access ?? [])) ? (
          <Button size="small" variant="contained" onClick={handleViewProfile}>
            {tu("View profile")}
          </Button>
        ) : undefined
      }
      caption="You can use this channel to speak to the candidate directly and invite them to an interview."
      subtitle={contextualMessage}
      sx={sx}
      title={tu("Application Received")}
    />
  );
}
